import {
  callApiPatch,
  callApiPost,
  constructApiUrl,
  createParams,
  validateParams,
  getChangedParams,
  getPolicyID,
  getRequestPreparePurchase,
} from './common';
import { createErrorBody } from '../error';
import { createAccident, updateAccident, prepareAccident } from '../../actions/Api';

const type = 'accident';

/**
 * Determines if the state allows us to run updates.
 * @param  {object} state The current state.
 * @return {boolean}      If we can update or not.
 */
const shouldUpdate = (state) => {
  return state.isUpdatingAccident ? state.isUpdatingAccident : true;
};

/**
 * Call the API using POST.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callAccidentApiPost = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Call the API using PATCH.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callAccidentApiPatch = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPatch(url, params, dispatch);
};

/**
 * Dispatches the create action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callCreateAccident = (service, params) => {
  return dispatch => {
    dispatch(createAccident());
    return callAccidentApiPost(service, params, dispatch);
  };
};

/**
 * Call the create  service.
 * @param  {Object} data The params to call the service with.
 * @return {Promise}     The promise of the service.
 */
export const requestCreateAccident = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const mappedParams = params ? createParams(params, formValues, getState()) : {};
      // Ensure that we have the correct paramters. Fail if not.
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      const service = '';
      return dispatch(callCreateAccident(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdateAccident = (service, params) => {
  return dispatch => {
    dispatch(updateAccident());
    return callAccidentApiPatch(service, params, dispatch);
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdateAccident = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      let mappedParams = params ? createParams(params, formValues, state) : {};
      const policyID = getPolicyID(state.userData, type);
      if (state.userData.accident) {
        // Find the changed values and pass those along.
        const additionalParams = { policyID };
        mappedParams = getChangedParams(mappedParams, state.userData.accident.usedParams, ['policyID'], additionalParams);
      }
      const service = `${policyID}`;
      return dispatch(callUpdateAccident(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the prepare action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPrepareAccident = (service, params) => {
  return dispatch => {
    dispatch(prepareAccident());
    return callAccidentApiPatch(service, params, dispatch);
  };
};

/**
 * Call the prepare purchase service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}     The promise of the service.
 */
export const requestPrepareAccident = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const prepare = getRequestPreparePurchase(type, getState(), formValues, params);
      return dispatch(callPrepareAccident(prepare.service, prepare.mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/utilities/api/accident.js